// Catalogue des scores déjà définis dans les autres ateliers, pour les
// reprendre au lieu de les retaper.
//
// UN SCORE N'EXISTE QUE DANS UN ATELIER : `score_dic` est embarqué dans chaque
// `games_config`, il n'y a pas de collection de scores. Or les mesures visent
// un score PAR SON IDENTIFIANT (`decision.score.<id>`) : un `onb` retapé à la
// main en `obn` ne reçoit aucun impact, sans que rien ne proteste. Reprendre
// le score d'un autre atelier, c'est reprendre son identifiant exact.
//
// Le même identifiant n'a pas toujours la même définition partout (`money`
// part de 5, 6, 7, 10 ou 25 selon l'atelier) : on ne choisit pas à la place de
// l'auteur, chaque définition distincte est une VARIANTE, avec les ateliers qui
// la portent. La plus répandue d'abord.

// Sérialisation à clés triées : deux définitions égales dont les champs ont été
// écrits dans un ordre différent sont la même variante.
const stableKey = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableKey).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${stableKey(value[k])}`).join(",")}}`;
  }
  return JSON.stringify(value);
};

/**
 * Les scores de `type` définis dans les ateliers autres que `currentConfigId`.
 * → [{ key, configCount, variants: [{ score, configs: [configId] }] }],
 *   les plus répandus d'abord. `configCount` compte les ateliers, pas les variantes.
 */
export function scoreCatalog(configs, currentConfigId, type) {
  const byKey = new Map();
  (configs || []).forEach((cfg) => {
    if (!cfg || cfg._id === currentConfigId) return;
    Object.entries(cfg.score_dic || {}).forEach(([key, score]) => {
      if (!score || score.type !== type) return;
      if (!byKey.has(key)) byKey.set(key, new Map());
      const variants = byKey.get(key);
      const sig = stableKey(score);
      if (!variants.has(sig)) variants.set(sig, { score, configs: [] });
      variants.get(sig).configs.push(cfg._id);
    });
  });

  return [...byKey.entries()]
    .map(([key, variants]) => {
      const list = [...variants.values()].sort((a, b) => b.configs.length - a.configs.length);
      return { key, configCount: list.reduce((n, v) => n + v.configs.length, 0), variants: list };
    })
    .sort((a, b) => b.configCount - a.configCount || a.key.localeCompare(b.key));
}

/** Filtre de recherche : identifiant, nom (de n'importe quelle variante) ou atelier. */
export function matchesScoreSearch(entry, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  if (entry.key.toLowerCase().includes(q)) return true;
  return entry.variants.some((v) =>
    (v.score.name || "").toLowerCase().includes(q)
    || v.configs.some((c) => c.toLowerCase().includes(q)));
}

/** Copie profonde : l'atelier reçoit sa propre définition, pas une référence partagée. */
export const copyScore = (score) => JSON.parse(JSON.stringify(score));
